import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useCallback, useMemo, useState } from "react";
import { CampaignStatus } from "../../types";
import {
  useAnalytics,
  usePartnerCampaignProducts,
  usePartnerCampaignsForAnalytics,
} from "../../hooks";
import { usePartnerCampaignBrands } from "../../hooks";
import { Slider, Loading, BarChartWithHeader } from "../../components";
import dayjs from "dayjs";
import { BookCheck, MousePointerClick, PackageOpen, Users } from "lucide-react";
import { useDataOpened, useDataRead, useDataSendOut } from "./hooks";
import { useTranslation } from "react-i18next"; // ✅ Import i18n
import { useDataClicked } from "./hooks/useDataClicked";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const animatedComponents = makeAnimated();

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top" as const,
    },
    title: {
      display: true,
      text: "Campaign Analytics",
    },
  },
};

const labels = ["January", "February", "March", "April", "May", "June", "July"];

export const data = {
  labels,
  datasets: [
    {
      label: "Permanent",
      data: [65, 59, 80, 81, 56, 55, 40],
      borderColor: "rgb(255, 99, 132)",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
    },
    {
      label: "Non Permanent",
      data: [28, 48, 40, 19, 86, 27, 90],
      borderColor: "rgb(53, 162, 235)",
      backgroundColor: "rgba(53, 162, 235, 0.5)",
    },
  ],
};

const MOCK_COUNTRIES = [
  { value: "SWE", label: "Sweden" },
  { value: "NL", label: "Netherlands" },
];

const GENDER_SELECT_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "None", label: "None" },
];



interface SelectOption<T extends string | number> {
  name: string;
  value: T;
}

const Analytics = () => {
  const { data } = usePartnerCampaignsForAnalytics();
  const { t } = useTranslation(); // ✅ Initialize translation hook
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [gender, setGender] = useState("");
  const [age, setAge] = useState<[number, number]>([27, 125]);
  const [startDate, setStartDate] = useState(new Date().toISOString());
  const [endDate, setEndDate] = useState(new Date().toISOString());

  const campaignSelectOptions = useMemo(() => {
    if (!data) {
      return;
    }

    return data.map((campaign) => ({
      label: `${campaign.name} ${t(`analytics.status.${campaign.status}`)}`,
      value: campaign.id,
    }));
  }, [data, t]);

  const { data: productsOptions } = usePartnerCampaignProducts();

  const { data: brandOptions } = usePartnerCampaignBrands();

  const {
    fetchAnalytics,
    isLoading,
    remove,
    data: analyticsData,
  } = useAnalytics({
    campaignIds: campaigns,
    startDate,
    endDate,
    ageBracket: age,
    brands,
    countries,
    gender,
    productIds: products,
  });

  const handleOnReset = useCallback(() => {
    setCampaigns([]);
    setProducts([]);
    setBrands([]);
    setCountries([]);
    setGender("");
    setAge([27, 125]);
    const date = new Date().toISOString();
    setStartDate(date);
    setEndDate(date);
    remove();
  }, [remove]);

  const dataSendOut = useDataSendOut(analyticsData);
  const dataRead = useDataRead(analyticsData);
  const dataOpened = useDataOpened(analyticsData);
  const dataClicked = useDataClicked(analyticsData);

  return (
    <div className="mt-16 pb-16">

      <section className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white shadow rounded-lg p-8">
          <div className="grid md:grid-cols-2 gap-6 mt-5">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-bold text-gray-700"
              >
                {t("analytics.start_date")}
              </label>
              <div className="mt-1">
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="p-2 border border-gray-300 rounded-md focus:outline-none w-full"
                  value={dayjs(startDate).format("YYYY-MM-DD")}
                  onChange={(event) =>
                    setStartDate(new Date(event.target.value).toISOString())
                  }
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-bold text-gray-700"
              >
                {t("analytics.end_date")}
              </label>
              <div className="mt-1">
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={dayjs(endDate).format("YYYY-MM-DD")}
                  className="p-2 border border-gray-300 rounded-md focus:outline-none w-full"
                  onChange={(event) =>
                    setEndDate(new Date(event.target.value).toISOString())
                  }
                />
              </div>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.campaigns")}
            </label>
            <div className="mt-1">
              <Select
                components={animatedComponents}
                options={campaignSelectOptions}
                isMulti
                placeholder={t("analytics.select_campaigns")}
                className="focus:outline-none w-full"
                onChange={(value) =>
                  setCampaigns(
                    (value as SelectOption<string>[]).map((item) => item.value)
                  )
                }
                isOptionDisabled={() => campaigns.length === 2}
                styles={{
                  option: (base, props) => ({
                    ...base,
                    ...(props.isDisabled && {
                      backgroundColor: "white",
                      opacity: 0.5,
                    }),
                  }),
                }}
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.products")}
            </label>
            <div className="mt-1">
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={productsOptions}
                placeholder={t("analytics.select_products")}
                className="focus:outline-none w-full"
                onChange={(value) =>
                  setProducts(
                    (value as SelectOption<string>[]).map((item) => item.value)
                  )
                }
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.brands")}
            </label>
            <div className="mt-1">
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={brandOptions}
                placeholder={t("analytics.select_brands")}
                className="focus:outline-none w-full"
                onChange={(value) =>
                  setBrands(
                    (value as SelectOption<string>[]).map((item) => item.value)
                  )
                }
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.countries")}
            </label>
            <div className="mt-1">
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={MOCK_COUNTRIES}
                placeholder={t("analytics.select_countries")}
                className="focus:outline-none w-full"
                onChange={(value) =>
                  setCountries(
                    (value as SelectOption<string>[]).map((item) => item.value)
                  )
                }
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.age")}
            </label>
            <div className="mt-1">
              <Slider
                min={18}
                max={125}
                onValueChange={(value) => setAge(value as [number, number])}
                value={age}
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-bold text-gray-700">
              {t("analytics.gender")}
            </label>
            <div className="mt-1">
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                options={GENDER_SELECT_OPTIONS}
                placeholder={t("analytics.select_gender")}
                className="focus:outline-none w-full"
                onChange={(value) =>
                  setGender((value as SelectOption<string>).value)
                }
              />
            </div>
          </div>
          <div className="mt-5">
            <button
              className="bg-indigo-600 text-white px-4 py-2 rounded-md focus:outline-none mr-2"
              onClick={() => fetchAnalytics()}
            >
              {t("analytics.filter")}
            </button>
            <button
              type="button"
              className="border border-indigo-600 text-indigo-600 px-4 py-2 rounded-md focus:outline-none"
              onClick={handleOnReset}
            >
              {t("analytics.reset")}
            </button>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-8 grid grid-cols-2 grid-rows-2 gap-4">
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <BarChartWithHeader
                data={dataSendOut.data}
                subtitle={t("analytics.metrics.send_out")}
                title={dataSendOut.subtitle?.toString()}
                icon={<Users />}
                label={t("analytics.metrics.send_out")}
                accessorLabel="Amount"
                suffix={undefined}
              />
              <BarChartWithHeader
                data={dataRead.data}
                subtitle={t("analytics.metrics.read")}
                title={dataRead.title?.toString()}
                icon={<BookCheck />}
                label={t("analytics.metrics.read")}
                extraAccessorKey="exactValue"
                extraAccessorLabel="Amount"
                min={0}
                max={100}
              />
              <BarChartWithHeader
                data={dataOpened.data}
                subtitle={t("analytics.metrics.opened")}
                title={dataOpened.title?.toString()}
                icon={<PackageOpen />}
                label={t("analytics.metrics.opened")}
                extraAccessorKey="exactValue"
                extraAccessorLabel="Amount"
                min={0}
                max={100}
              />
              <BarChartWithHeader
                data={dataClicked.data}
                subtitle={t("analytics.metrics.clicked")}
                title={dataClicked.title?.toString()}
                icon={<MousePointerClick />}
                label={t("analytics.metrics.clicked")}
                extraAccessorKey="exactValue"
                extraAccessorLabel="Amount"
                min={0}
                max={100}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Analytics;
